import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { LayoutDashboard, Users, BarChart3, Bot, Bell, FileText, Activity, LogOut, Menu, X } from 'lucide-react';

const navItems = [
  { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/admin/users', icon: Users, label: 'Brukere' },
  { to: '/admin/analytics', icon: BarChart3, label: 'Analyse' },
  { to: '/admin/coach', icon: Bot, label: 'Coach Config' },
  { to: '/admin/notifications', icon: Bell, label: 'Varsler' },
  { to: '/admin/templates', icon: FileText, label: 'Maler' },
  { to: '/admin/system', icon: Activity, label: 'System' },
];

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const SidebarContent = () => (
    <>
      <div className="p-5 border-b border-white/5">
        <a href="/" className="font-['Barlow_Condensed'] font-bold text-xl tracking-wider text-white">TEMPO</a>
        <p className="text-[10px] text-white/30 tracking-[0.18em] uppercase mt-1">Admin</p>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`admin-nav-${item.to.split('/').pop()}-link`}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-[background-color,color,border-color] duration-180 ${
                isActive
                  ? 'bg-[#CCFF00]/10 text-[#CCFF00] border-l-2 border-[#CCFF00]'
                  : 'text-white/50 hover:text-white/80 hover:bg-white/5 border-l-2 border-transparent'
              }`
            }
          >
            <item.icon className="w-4 h-4" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3 mb-3 px-2">
          <div className="w-8 h-8 rounded-full bg-[#CCFF00]/10 flex items-center justify-center">
            <span className="text-xs font-bold text-[#CCFF00]">{admin?.name?.charAt(0) || 'A'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-white truncate">{admin?.name || 'Admin'}</p>
            <p className="text-[10px] text-white/30 uppercase tracking-wider">{admin?.role || 'admin'}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="admin-logout-button"
          className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-white/40 hover:text-white/70 hover:bg-white/5 transition-[background-color,color] duration-180"
        >
          <LogOut className="w-4 h-4" /> Logg ut
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-sidebar">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-[#0A0A0A] border-r border-white/5 flex flex-col">
            <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 text-white/40 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            <SidebarContent />
          </div>
        </div>
      )}
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:w-60 lg:flex-col bg-[#0A0A0A] border-r border-white/5">
        <SidebarContent />
      </aside>
      {/* Main content */}
      <div className="lg:pl-60">
        {/* Top header */}
        <header className="sticky top-0 z-40 h-14 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5 flex items-center px-6 gap-4">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-white/50 hover:text-white">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <span className="text-xs text-white/30">{admin?.email}</span>
        </header>
        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
